/**
 * Gamification configuration for the **Apunte Digital** domain.
 *
 * The engine (`engine.ts`) is agnostic; this declares WHAT this app rewards,
 * with a GENTLE productivity tone: creating notes (pages) and working on them
 * (editing blocks). The `notes` streak counts days you worked on your
 * notes. Positive and optional; it never punishes.
 *
 * `PlayerState` is recomputable on the client from already synced pages +
 * blocks (local-first): it requires no new columns. Pure → re-derivable
 * on the server (anti-fraud).
 */

import { counterAtLeast, levelAtLeast, streakAtLeast } from "./badges";
import { type GamificationConfig, applyEvents, initialState } from "./engine";
import type { DayKey, GameEvent, PlayerState } from "./types";

/** Streak line: days with activity (editing any block). */
export const NOTES_STREAK_LINE = "notes";

export const APUNTE_EVENT = {
  pageCreated: "page.created",
  blockEdited: "block.edited",
} as const;

export const XP_PER_PAGE = 20;
export const XP_PER_BLOCK = 2;

export const APUNTE_GAMIFICATION_CONFIG: GamificationConfig = {
  xp: {
    [APUNTE_EVENT.pageCreated]: XP_PER_PAGE,
    [APUNTE_EVENT.blockEdited]: XP_PER_BLOCK,
  },
  streakLines: {
    [APUNTE_EVENT.blockEdited]: [NOTES_STREAK_LINE],
  },
  counters: {
    [APUNTE_EVENT.pageCreated]: ["pages"],
    [APUNTE_EVENT.blockEdited]: ["blocks"],
  },
  badges: [
    {
      id: "first-note",
      title: "Primera nota",
      when: counterAtLeast("pages", 1),
    },
    {
      id: "pages-10",
      title: "Diez notas",
      when: counterAtLeast("pages", 10),
      bonusXp: 30,
    },
    {
      id: "pages-50",
      title: "Cuaderno lleno",
      when: counterAtLeast("pages", 50),
      bonusXp: 90,
    },
    {
      id: "blocks-100",
      title: "100 bloques",
      when: counterAtLeast("blocks", 100),
      bonusXp: 40,
    },
    {
      id: "blocks-500",
      title: "Prolífico",
      when: counterAtLeast("blocks", 500),
      bonusXp: 120,
    },
    {
      id: "notes-streak-3",
      title: "3 días de apuntes",
      when: streakAtLeast(NOTES_STREAK_LINE, 3),
      bonusXp: 15,
    },
    {
      id: "notes-streak-7",
      title: "Semana productiva",
      when: streakAtLeast(NOTES_STREAK_LINE, 7),
      bonusXp: 40,
    },
    {
      id: "notes-streak-30",
      title: "Mes productivo",
      when: streakAtLeast(NOTES_STREAK_LINE, 30),
      bonusXp: 120,
    },
    { id: "level-5", title: "Nivel 5", when: levelAtLeast(5) },
    { id: "level-10", title: "Nivel 10", when: levelAtLeast(10) },
  ],
  quests: [
    {
      id: "weekly-notes",
      title: "Crea 3 notas esta semana",
      period: "weekly",
      goals: [{ metric: APUNTE_EVENT.pageCreated, target: 3 }],
      bonusXp: 40,
    },
  ],
};

export interface PageInput {
  /** ISO creation time of the page/note. */
  createdAt?: string | null;
}

export interface BlockInput {
  /** ISO time of the block's last edit (defines the activity day). */
  updatedAt?: string | null;
}

function dayOfIso(iso: string | null | undefined): DayKey | null {
  if (!iso || iso.length < 10) {
    return null;
  }
  return iso.slice(0, 10);
}

/**
 * Derives `GameEvent`s from pages (creation) and blocks (editing). Every
 * page scores; every block scores and activates the `notes` activity streak
 * (the engine deduplicates by day). Sorts by day for determinism.
 */
export function deriveApunteGameEvents(input: {
  pages: Iterable<PageInput>;
  blocks: Iterable<BlockInput>;
}): GameEvent[] {
  const events: GameEvent[] = [];

  for (const p of input.pages) {
    const day = dayOfIso(p.createdAt);
    if (!day) {
      continue;
    }
    events.push({
      type: APUNTE_EVENT.pageCreated,
      at: Date.parse(`${day}T12:00:00.000Z`),
      day,
    });
  }

  for (const b of input.blocks) {
    const day = dayOfIso(b.updatedAt);
    if (!day) {
      continue;
    }
    events.push({
      type: APUNTE_EVENT.blockEdited,
      at: Date.parse(`${day}T12:00:00.000Z`),
      day,
    });
  }

  events.sort((a, b) => (a.day === b.day ? 0 : (a.day ?? "") < (b.day ?? "") ? -1 : 1));
  return events;
}

/** Reconstructs `PlayerState` from pages + blocks (local-first, pure). */
export function computeApuntePlayerState(
  input: { pages: Iterable<PageInput>; blocks: Iterable<BlockInput> },
  opts: { freezes?: number } = {},
): PlayerState {
  const events = deriveApunteGameEvents(input);
  return applyEvents(initialState(opts.freezes ?? 0), events, APUNTE_GAMIFICATION_CONFIG).state;
}
